import React, {useCallback, useMemo, memo} from 'react';
import {v4 as uuid} from 'uuid';
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import {IOption, TSatisfaction} from 'shared/services/api/quiz/Quiz';
import {OptionColumn} from './OptionColumn';
import {useQuiz} from 'pages/quiz/hooks';

interface ISatisfactionPercentageConfigProps {
  value: IOption[];
  onChange: (value: IOption[], isInitialFix?: boolean) => void;
}

export const SatisfactionPercentageConfig: React.FC<ISatisfactionPercentageConfigProps> =
  memo(({value, onChange}) => {
    const {hasInviteSent} = useQuiz();
    const theme = useTheme();
    const options = useMemo(() => {
      if (value.length === 0) {
        value.push(
          {
            id: `newOption__${uuid()}`,
            description: '',
            satisfaction: 'satisfeito',
            order: 1,
          },
          {
            id: `newOption__${uuid()}`,
            description: '',
            satisfaction: 'satisfeito',
            order: 2,
          },
          {
            id: `newOption__${uuid()}`,
            description: '',
            satisfaction: 'satisfeito',
            order: 3,
          },
          {
            id: `newOption__${uuid()}`,
            description: '',
            satisfaction: 'insatisfeito',
            order: 4,
          },
          {
            id: `newOption__${uuid()}`,
            description: '',
            satisfaction: 'insatisfeito',
            order: 5,
          },
          {
            id: `newOption__${uuid()}`,
            description: '',
            satisfaction: 'insatisfeito',
            order: 6,
          },
        );
      }
      return value.sort((a, b) => a.order - b.order);
    }, [value]);

    const endWith = useMemo(() => {
      return options[options.length - 1].order || 6;
    }, [options]);

    const handleChangeEndWith = useCallback(
      (end: number) => {
        const result = end - options.length;

        if (result === 0) return;

        if (result < 0) {
          Array.from(Array(result * -1).keys()).forEach(() => {
            options.pop();
          });
        } else {
          Array.from(Array(result).keys()).forEach(() => {
            options.push({
              id: `newOption__${uuid()}`,
              description: '',
              order: options.length + 1,
            });
          });
        }

        onChange([...options]);
      },
      [onChange, options],
    );

    const handleDescriptionChange = useCallback(
      (index: number, text: string) => {
        options[index - 1].description = text;
        onChange([...options]);
      },
      [onChange, options],
    );

    const handleValueChange = useCallback(
      (index: number, value: TSatisfaction, isInitialFix?: boolean) => {
        options[index - 1].satisfaction = value;
        onChange([...options], isInitialFix);
      },
      [onChange, options],
    );

    const handleOnDragEnd = useCallback(
      (result: DropResult) => {
        if (!result.destination) return;

        const [reorderedItem] = options.splice(result.source.index, 1);
        options.splice(result.destination.index, 0, reorderedItem);
        var count = 1;
        options.forEach((item) => {
          item.order = count;
          count++;
        });

        onChange([...options]);
      },
      [onChange, options],
    );

    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={1}>
            <FormControl variant="outlined" fullWidth>
              <Select
                value={endWith}
                onChange={(e) => handleChangeEndWith(Number(e.target.value))}>
                <MenuItem disabled={hasInviteSent && 2 <= endWith} value={2}>
                  2
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 3 <= endWith} value={3}>
                  3
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 4 <= endWith} value={4}>
                  4
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 5 <= endWith} value={5}>
                  5
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 6 <= endWith} value={6}>
                  6
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 7 <= endWith} value={7}>
                  7
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 8 <= endWith} value={8}>
                  8
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 9 <= endWith} value={9}>
                  9
                </MenuItem>
                <MenuItem disabled={hasInviteSent && 10 <= endWith} value={10}>
                  10
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Colunas</Typography>
          </Grid>
          <Grid item xs={8}>
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="droppable-1">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {options.map((option, index) =>
                      option.order !== 0 ? (
                        <Draggable
                          key={`satisfaction-percentage-option-${option.order}`}
                          draggableId={'draggable-' + option.order}
                          index={index}>
                          {(provided, {isDragging}) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                background: theme.palette.background.paper,
                                boxShadow: isDragging
                                  ? theme.shadows[4]
                                  : undefined,
                                paddingRight: isDragging
                                  ? theme.spacing(3)
                                  : theme.spacing(0),
                                paddingTop: isDragging
                                  ? theme.spacing(1.5)
                                  : theme.spacing(2),
                              }}>
                              <OptionColumn
                                isEdit={option.satisfaction !== undefined}
                                option={option}
                                endsWith={endWith}
                                onChangeDescription={handleDescriptionChange}
                                onChangeValue={handleValueChange}
                              />
                            </div>
                          )}
                        </Draggable>
                      ) : (
                        <Box
                          key={'satisfaction-percentage-option-0'}
                          alignItems={'center'}>
                          <OptionColumn
                            isEdit={option.satisfaction !== undefined}
                            option={option}
                            endsWith={endWith}
                            onChangeDescription={handleDescriptionChange}
                            onChangeValue={handleValueChange}
                          />
                        </Box>
                      ),
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>
        </Grid>
      </Box>
    );
  });
